import {
  faCube,
  faPuzzlePiece,
  faRocketLaunch,
} from '@fortawesome/pro-light-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { css as beautifyCss, html as beautifyHtml } from 'js-beautify';

import {
  CartChangeDetectionMethod,
  CheckoutPlusAction,
  CheckoutPlusElementKind,
  CheckoutPlusInsertionStrategy,
  CheckoutPlusRefreshMethod,
  CheckoutPlusTemplateVariable,
  CrewMerchantUi,
  isTruthy,
  Pegasus,
  WIDGET_DEFAULTS,
  WidgetConfig,
} from 'corso-types';

import {
  Action,
  Alert,
  Badge,
  Card,
  ClickToCopy,
  ConfirmModal,
  Disclosure,
  FloatingSave,
  Icon,
  Label,
  Modal,
  MultiSelect,
  OrderLineItem,
  Page,
  SimpleSelect,
  SimpleSummary,
  SpinnerIcon,
  SupportedIcon,
  SwitchInput,
  TextAreaInput,
  TextInput,
  UrlInput,
} from 'corso-admin-ui';
import { useCurrency } from '~/hooks/useCurrency';
import { formatter } from 'corso-web-lib';
import { FormEventHandler, useRef, useState, type ReactNode } from 'react';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'react-hook-form';
import ClipboardButton from '~/components/ClipboardButton';
import { AutomationListByEventType } from '~/features/settings/automations/AutomationListByEventType';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import { useFactValues } from '~/features/settings/hooks/useFactValues';
import {
  useCheckoutPlusEmbed,
  useConfigSettings,
  useConfigSettingsUpdate,
  useConfigureGsp,
} from '~/hooks/useConfigSettings';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import { useStoreId } from '~/providers/StoreUserProvider';
import { shipProtectSettingsFormSchema, UseFormTypes } from '~/types';
import { CheckoutWidgetCard } from './CheckoutWidgetCard';

type Placement = NonNullable<
  NonNullable<NonNullable<WidgetConfig['checkoutPlus']>['shopNow']>['placement']
>;

const Placement = {
  'bottom left': 'bottom left',
  'bottom right': 'bottom right',
} as const satisfies {
  [K in Placement]: K;
};

const placementLabel = {
  'bottom left': 'Bottom Left',
  'bottom right': 'Bottom Right',
} as const satisfies Record<Placement, string>;
function useCheckoutPlusForm() {
  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );

  return useForm({
    resolver: zodResolver(shipProtectSettingsFormSchema),
    values: data,
  });
}

function useCheckoutPlusFormContext() {
  type FormType = UseFormTypes<typeof useCheckoutPlusForm>;
  return useFormContext<
    FormType['FieldValues'],
    FormType['Context'],
    FormType['TransformedValues']
  >();
}

const checkoutPlusSettingsFormId = 'checkout-plus-settings';

const insertionStrategyText = {
  [CheckoutPlusInsertionStrategy.insertAdjacentAfterBegin]:
    'Insert inside the element, at the top',
  [CheckoutPlusInsertionStrategy.insertAdjacentBeforeEnd]:
    'Insert inside the element, at the bottom',
  [CheckoutPlusInsertionStrategy.insertAdjacentBeforeBegin]:
    'Insert before the element',
  [CheckoutPlusInsertionStrategy.insertAdjacentAfterEnd]:
    'Insert after the element',
} satisfies Record<CheckoutPlusInsertionStrategy, string>;

const insertStrategyOptions = Object.entries(insertionStrategyText).map(
  ([value, label]) => ({ value, label }),
);

const checkoutActionText = {
  [CheckoutPlusAction.addVariantToCart]: 'Add product to cart',
  [CheckoutPlusAction.setShippingPlusOptOut]:
    'Apply discount to a Shipping Plus rate',
  [CheckoutPlusAction.setShippingPlusReorderRate]:
    'Default to a Shipping Plus Rate',
} satisfies Record<CheckoutPlusAction, string>;

const checkoutActionOptions = Object.entries(checkoutActionText).map(
  ([value, label]) => ({ value, label }),
);

const checkoutPlusElementKindText = {
  [CheckoutPlusElementKind.altCheckoutButton]: 'Opt-Out Checkout Button',
  [CheckoutPlusElementKind.checkoutInfo]: 'Checkout Plus Info',
} satisfies Record<CheckoutPlusElementKind, string>;

const checkoutPlusElementKindOptions = Object.entries(
  checkoutPlusElementKindText,
).map(([value, label]) => ({ value, label }));

const cartChangeDetectionMethodText = {
  [CartChangeDetectionMethod.cartWatcher]: 'Cart Watcher',
  [CartChangeDetectionMethod.selectorObserver]: 'Selector Observer',
} satisfies Record<CartChangeDetectionMethod, string>;

const cartChangeDetectionMethodOptions = Object.entries(
  cartChangeDetectionMethodText,
).map(([value, label]) => ({ value, label }));

const formatCss = (css: string) => beautifyCss(css, { indent_size: 2 });

const formatHtml = (html: string) =>
  beautifyHtml(
    html.replace(/>\s*</g, '>\n<'), // Add newlines between tags
    {
      indent_size: 2, // Number of spaces for indentation
      wrap_line_length: 80, // Maximum line length
      indent_inner_html: true, // Indent inner HTML for proper hierarchy
      unformatted: [], // Ensure all tags are formatted
      content_unformatted: [], // Ensure content inside tags is also formatted
    },
  );

function CheckoutPlusElements() {
  const methods = useCheckoutPlusFormContext();
  const isCorsoAdmin = useIsCorsoAdmin();

  const {
    fields: elementFields,
    append: appendElement,
    remove: removeElement,
  } = useFieldArray({
    control: methods.control,
    name: 'widgetConfig.checkoutPlus.elements',
  });

  const elementsState = methods.getFieldState(
    'widgetConfig.checkoutPlus.elements',
  );

  return (
    <SettingsAnchorCard id="checkout-plus-elements">
      <div className="flex flex-row items-center justify-between">
        <Card.Heading>Checkout Plus Elements</Card.Heading>
        <Action
          icon="add"
          onClick={() =>
            appendElement(WIDGET_DEFAULTS.checkoutPlus.elements[0])
          }
          accessibilityLabel="Add Checkout Plus Element"
          variant="ghost"
        />
      </div>
      {elementsState.error && (
        <Alert
          variant="critical"
          heading={
            elementsState.error.message ||
            elementsState.error.root?.message ||
            'Checkout Elements Error'
          }
        />
      )}

      {elementFields.map((field, index) => {
        // You can run any custom logic here before rendering the JSX
        // For example: console.log('Rendering field', index);
        const selectedKind = methods.watch(
          `widgetConfig.checkoutPlus.elements.${index}.kind`,
        );

        return (
          <div
            key={field.id}
            className="m-2 flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-6 shadow-xs"
          >
            <Controller
              name={`widgetConfig.checkoutPlus.elements.${index}.kind`}
              control={methods.control}
              render={({ field: { onChange, value }, fieldState }) => (
                <SimpleSelect
                  label="Type"
                  details="The type of Checkout Plus element to display."
                  options={checkoutPlusElementKindOptions}
                  value={value} // be warned, react-hook-form is lying about the value type, as there's no default value
                  onChange={onChange}
                  error={fieldState.error?.message}
                  required
                />
              )}
            />

            <Controller
              control={methods.control}
              name={`widgetConfig.checkoutPlus.elements.${index}.selectors`}
              defaultValue={[]}
              render={({ field: f, fieldState }) => (
                <MultiSelect
                  creatable
                  label="Selectors"
                  options={[]}
                  details="The CSS selectors for the Checkout Plus element."
                  placeholder="Add Selector"
                  value={f.value.map((v) => ({
                    value: v.toString(),
                    label: v,
                  }))}
                  onChange={(o) => f.onChange(o.map((v) => v.value))}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name={`widgetConfig.checkoutPlus.elements.${index}.insertionStrategy`}
              control={methods.control}
              render={({ field: { onChange, value }, fieldState }) => (
                <SimpleSelect
                  label="Insert Strategy"
                  details="Where to insert the Checkout Plus element, relative to the selectors."
                  options={insertStrategyOptions}
                  value={value} // be warned, react-hook-form is lying about the value type, as there's no default value
                  onChange={onChange}
                  error={fieldState.error?.message}
                  required
                />
              )}
            />

            {isCorsoAdmin && (
              <TextInput
                id="checkout-plus-element-title"
                required
                label={
                  selectedKind === CheckoutPlusElementKind.altCheckoutButton
                    ? 'Button Title'
                    : 'Title'
                }
                {...methods.register(
                  `widgetConfig.checkoutPlus.elements.${index}.title`,
                )}
                error={
                  methods.formState.errors.widgetConfig?.checkoutPlus
                    ?.elements?.[index]?.title?.message
                }
              />
            )}

            {selectedKind === CheckoutPlusElementKind.checkoutInfo && (
              <>
                <TextInput
                  id="checkout-plus-element-description"
                  label="Description"
                  {...methods.register(
                    `widgetConfig.checkoutPlus.elements.${index}.description`,
                  )}
                  error={
                    methods.formState.errors.widgetConfig?.checkoutPlus
                      ?.elements?.[index]?.description?.message
                  }
                />

                <Controller
                  name={`widgetConfig.checkoutPlus.elements.${index}.imgUrl`}
                  control={methods.control}
                  render={({ field: { onChange, value }, fieldState }) => (
                    <UrlInput
                      id="checkout-plus-image-url"
                      label="Image URL"
                      value={value}
                      onChange={onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />
                <TextAreaInput
                  id="checkout-plus-image-html-content"
                  label="Image HTML Content"
                  details="The HTML content to display in place of the image. This will override the image URL."
                  {...methods.register(
                    `widgetConfig.checkoutPlus.elements.${index}.imgHtmlContent`,
                  )}
                />
              </>
            )}

            <div className="mt-2 self-end">
              <Action
                onClick={() => removeElement(index)}
                variant="destructive"
              >
                Remove
              </Action>
            </div>
          </div>
        );
      })}
    </SettingsAnchorCard>
  );
}

const refreshMethodText = {
  [CheckoutPlusRefreshMethod.reloadPage]: 'Reload Page',
  [CheckoutPlusRefreshMethod.none]: 'None',
} satisfies Record<CheckoutPlusRefreshMethod, string>;

const refreshMethodOptions = Object.values(CheckoutPlusRefreshMethod).map(
  (v) => ({
    value: v,
    label: refreshMethodText[v],
  }),
);

function AdvancedSettings({
  show,
  onClose,
  isCheckoutPlusScriptConfigured,
  checkoutPlusScriptUrl,
}: {
  show: boolean;
  onClose: () => void;
  isCheckoutPlusScriptConfigured: boolean;
  checkoutPlusScriptUrl?: string | null;
}) {
  const methods = useCheckoutPlusFormContext();
  const [showDirectCheckoutModal, setShowDirectCheckoutModal] = useState(false);

  const { mutate: configureSp, isPending: isConfiguringSp } = useConfigureGsp();

  const { data: factValuesData } = useFactValues();

  const skus = factValuesData?.arbitrary['product/sku'] ?? [];
  const customerTags = factValuesData?.arbitrary['customer/tags'] ?? [];

  const skuOptions = skus.map((sku) => ({
    value: sku,
    label: sku,
  }));

  const customerTagOptions = customerTags.map((tag) => ({
    value: tag,
    label: tag,
  }));

  const removeProtectionProductOnPageLoad = methods.watch(
    'widgetConfig.checkoutPlus.removeProtectionProductOnPageLoad',
  );

  return (
    <Modal
      size="xl"
      show={show}
      onClose={onClose}
      title="Advanced Settings"
      actions={
        <>
          <Action onClick={onClose}>Cancel</Action>
          <Action
            variant="primary"
            type="submit"
            disabled={!methods.formState.isDirty}
            form={checkoutPlusSettingsFormId}
            onClick={() => {
              onClose();
            }}
          >
            Save
          </Action>
        </>
      }
    >
      <Alert heading="Corso Admins Only">
        The following settings are only visible to Corso admin users.
      </Alert>

      <Card>
        <AutomationListByEventType
          eventType={Pegasus.EventType.merchantPaidShippingProtection}
        />
      </Card>

      <Card>
        <Card.Heading>Dangerous Actions</Card.Heading>

        <div className="flex flex-row flex-wrap gap-2">
          <Action
            variant="caution"
            onClick={() => {
              configureSp(
                isCheckoutPlusScriptConfigured
                  ? 'deleteCheckoutPlusScript'
                  : 'createCheckoutPlusScript',
              );
              onClose();
            }}
          >
            {isCheckoutPlusScriptConfigured ? 'Remove' : 'Add'} Legacy Script
          </Action>
          <Action
            variant="caution"
            onClick={() => configureSp('checkoutPlusProducts')}
            loading={isConfiguringSp}
          >
            Recreate Products
          </Action>
        </div>
      </Card>

      <Card>
        <Card.Heading>Direct Checkout Escape Hatch</Card.Heading>
        <SwitchInput
          id="is-direct-checkout-enabled"
          label="Direct Checkout"
          details={
            <>
              When enabled, the Checkout Plus widget navigates directly to{' '}
              <code>/checkout</code> instead of clicking the underlying checkout
              button.
            </>
          }
          checked={
            !!methods.watch('widgetConfig.checkoutPlus.isDirectCheckoutEnabled')
          }
          onChange={(e) => {
            if (e.target.checked) {
              setShowDirectCheckoutModal(true);
            } else {
              methods.setValue(
                'widgetConfig.checkoutPlus.isDirectCheckoutEnabled',
                false,
                { shouldDirty: true },
              );
            }
          }}
        />
      </Card>

      {checkoutPlusScriptUrl && (
        <Card>
          <Card.Line>
            <Card.Heading>Headless Script Tag</Card.Heading>
            <ClipboardButton
              onClick={() =>
                `<script async src="${checkoutPlusScriptUrl}"></script>`
              }
              variant="ghost"
            />
          </Card.Line>

          <code className="flex-1 rounded bg-neutral-100 px-3 py-2 text-xs break-all">
            {`<script async src="${checkoutPlusScriptUrl}"></script>`}
          </code>
        </Card>
      )}

      <Card>
        <Card.Heading>Checkout Extension Product</Card.Heading>
        <Controller
          control={methods.control}
          name="widgetConfig.checkoutExtensionProduct.isDefaultOptIn"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="checkout-extension-product-default-opt-in"
              label="Default Opt-In"
              details="When enabled, customers will be opted in to product protection by default at checkout when no checkout plus attributes are present."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Card>

      <Card>
        <TextInput
          id="script-tag-note"
          label="Admin Script Note"
          details="A note to indicate a script tag placement outside of the managed script tags provided by Shopify. e.g Headless Store, Non-Standard Cart, etc."
          {...methods.register(`widgetConfig.checkoutPlus.scriptTagNote`)}
          error={
            methods.formState.errors.widgetConfig?.checkoutPlus?.scriptTagNote
              ?.message
          }
        />

        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.disabledUrls"
          render={({ field: { onChange, value }, fieldState }) => (
            <MultiSelect
              label="Disabled URLs"
              details="Any URLs that match, or include, will disable shipping protection from running."
              creatable
              clearable
              options={[]}
              value={value?.map((url) => ({ label: url, value: url })) ?? []}
              onChange={(selected) => onChange(selected.map((s) => s.value))}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.cloneOmitAttributes"
          render={({ field: { onChange, value }, fieldState }) => (
            <MultiSelect
              label="Clone Omit Attributes"
              details={
                <p>
                  Additional case-insensitive HTML attributes to strip from
                  cloned checkout buttons, on top of <code>onclick</code> and{' '}
                  <code>href</code> which are always removed. Assigning{' '}
                  <code>class</code>, <code>style</code>, <code>id</code>,{' '}
                  <code>type</code>, or <code>disabled</code> will be ignored as
                  they are always preserved to ensure proper styling and
                  uniqueness.
                </p>
              }
              creatable
              clearable
              options={[]}
              value={value?.map((attr) => ({ label: attr, value: attr })) ?? []}
              onChange={(selected) => onChange(selected.map((s) => s.value))}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.disabledCustomerTags"
          render={({ field: f, fieldState }) => (
            <MultiSelect
              creatable
              options={customerTagOptions}
              label="Disabled Customer Tags"
              details="If any of these tags are present on the customer, Checkout Plus will not be displayed."
              value={
                f.value?.map((v) => ({
                  value: v.toString(),
                  label: v,
                })) ?? []
              }
              onChange={(o) => f.onChange(o.map((v) => v.value))}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.hideWidgetSkus"
          render={({ field: f, fieldState }) => (
            <MultiSelect
              creatable
              label="Hide Widget SKUs"
              options={skuOptions}
              details="Any SKUs that contain these values will not be displayed in the Checkout Plus widget."
              value={
                f.value?.map((v) => ({
                  value: v.toString(),
                  label: v,
                })) ?? []
              }
              onChange={(o) => f.onChange(o.map((v) => v.value))}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.shouldQueryShadowDom"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="should-query-shadow-dom"
              label="Query Shadow DOM"
              details="When enabled, Checkout Plus will query the shadow-sm DOM for the specified selectors, this setting can invoke fear of the unknown."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.shouldProtectCompanyCarts"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="should-protect-company-carts"
              label="Protect Company Carts"
              details="When enabled, Checkout Plus will attempt to protect carts with an attached Purchasing Company. Typically used for B2B or wholesale scenarios."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.isQueryAjaxCartApiEnabled"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="is-query-ajax-cart-api-enabled"
              label="Query Ajax Cart API"
              details="When enabled, Checkout Plus will attempt to query the Ajax Cart API for the cart ID when it is not found in the cookie, local storage, or session storage. This can be disabled on stores that do not expose the Ajax Cart API."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      </Card>

      <Card>
        <Card.Heading>Protection Product Removal</Card.Heading>
        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.removeProtectionProductOnPageLoad"
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="refresh-cart-on-protection-removal"
              label="Remove Protection Product On Page Load"
              details="When enabled, Checkout Plus will remove the protection product from the cart when the page loads."
              checked={!!value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />

        {removeProtectionProductOnPageLoad && (
          <Controller
            name="widgetConfig.checkoutPlus.cartRefreshMethod"
            control={methods.control}
            render={({ field: { onChange, value }, fieldState }) => (
              <SimpleSelect
                label="Cart Refresh Method"
                details="The method to use to refresh the cart when the protection product is removed."
                options={refreshMethodOptions}
                value={value}
                onChange={onChange}
                error={fieldState.error?.message}
                required
              />
            )}
          />
        )}
      </Card>

      <Card>
        <Card.Heading>Checkout Button</Card.Heading>

        <TextInput
          id="cart-retrieval-key"
          required
          label="Cart Retrieval Key"
          {...methods.register(`widgetConfig.checkoutPlus.cartRetrievalKey`)}
          error={
            methods.formState.errors.widgetConfig?.checkoutPlus
              ?.cartRetrievalKey?.message
          }
        />

        <Action
          variant="default"
          onClick={() =>
            methods.setValue(
              'widgetConfig.checkoutPlus.checkoutButtonText',
              formatHtml(
                methods.getValues(
                  'widgetConfig.checkoutPlus.checkoutButtonText',
                ) ?? '',
              ),
              {
                shouldDirty: true,
              },
            )
          }
        >
          Format HTML
        </Action>
      </Card>

      <Card>
        <Card.Heading>Shop Now</Card.Heading>
        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.shopNow.placement"
          render={({ field: { onChange, value }, fieldState }) => (
            <SimpleSelect
              label="Shop Now Placement"
              details="The location where the floating button appears on the page."
              options={Object.values(Placement).map((placement) => ({
                label: placementLabel[placement],
                value: placement,
              }))}
              value={value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={methods.control}
          name="widgetConfig.checkoutPlus.shopNow.hideSelectors"
          render={({ field: f, fieldState }) => (
            <MultiSelect
              creatable
              options={[]}
              label="Shop Now Hide Selectors"
              details="The CSS selectors for elements that should be hidden when a Shop Now session is detected. This is done as an alternative to using the CSS in the custom styles, since that is always applied with Checkout Plus."
              placeholder="Add Selector"
              value={
                f.value?.map((v) => ({
                  value: v.toString(),
                  label: v,
                })) ?? []
              }
              onChange={(o) => f.onChange(o.map((v) => v.value))}
              error={fieldState.error?.message}
            />
          )}
        />
        <TextAreaInput
          id="shop-now-checkout-button-text"
          label="Content"
          details="The text or HTML to display within the checkout button on a Shop Now session."
          {...methods.register(
            'widgetConfig.checkoutPlus.shopNow.checkoutButtonText',
          )}
          error={
            methods.formState.errors.widgetConfig?.checkoutPlus?.shopNow
              ?.checkoutButtonText?.message
          }
        />
      </Card>

      <ConfirmModal
        title="Enable Direct Checkout Escape Hatch"
        prompt="This will bypass any additional behavior that an underlying checkout button may have, and will navigate directly to the checkout page. Enable only if you have shown this to be necessary."
        show={showDirectCheckoutModal}
        confirmText="Enable Direct Checkout"
        variant="caution"
        onConfirm={() => {
          methods.setValue(
            'widgetConfig.checkoutPlus.isDirectCheckoutEnabled',
            true,
            { shouldDirty: true },
          );
          setShowDirectCheckoutModal(false);
        }}
        onCancel={() => setShowDirectCheckoutModal(false)}
      />
    </Modal>
  );
}

function SetupStep({
  icon,
  title,
  description,
  complete,
  action,
  learnMoreUrl,
}: {
  icon: SupportedIcon;
  title: string;
  description: string;
  complete: boolean;
  action?: ReactNode;
  learnMoreUrl?: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-neutral-200 p-3">
      <div
        className={`mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full ${
          complete
            ? 'bg-green-100 text-green-600'
            : 'bg-neutral-100 text-neutral-500'
        }`}
      >
        {complete ? (
          <Icon icon="success" className="size-5" />
        ) : (
          <Icon icon={icon} className="size-5" />
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={`text-sm font-medium ${complete ? 'text-neutral-400 line-through' : ''}`}
        >
          {title}
        </span>
        <span className="text-xs text-neutral-500">
          {description}
          {learnMoreUrl && (
            <>
              {' '}
              <Action variant="link" to={learnMoreUrl} target="_blank">
                Learn more
              </Action>
            </>
          )}
        </span>
      </div>
      <div className="shrink-0 self-center">
        {complete ? <Badge variant="success">Done</Badge> : action}
      </div>
    </div>
  );
}

function SimpleSetupSteps({
  isEmbedConfigured,
  onActivateEmbed,
  configuredProducts,
  isCheckoutPlusEnabled,
}: {
  isEmbedConfigured: boolean;
  onActivateEmbed: () => void;
  configuredProducts: number;
  isCheckoutPlusEnabled: boolean;
}) {
  const { mutate: configureSp, isPending } = useConfigureGsp();

  return (
    <Card>
      <Disclosure
        defaultOpen
        renderSummary={<SimpleSummary>Simple Setup Steps</SimpleSummary>}
      >
        <div className="flex flex-col gap-2 pb-2">
          <SetupStep
            icon={faPuzzlePiece}
            title="Activate Theme App Embed"
            description="Enable the Checkout Plus embed on your theme. Make sure to save the theme if it doesn't save automatically."
            complete={isEmbedConfigured}
            action={
              <Action variant="primary" onClick={onActivateEmbed}>
                Activate
              </Action>
            }
          />
          <SetupStep
            icon={faCube}
            title="Create Variants in Shopify"
            description="Configure the product variants used by Checkout Plus. Existing products will be recreated."
            complete={configuredProducts > 0}
            action={
              <Action
                variant="primary"
                onClick={() => configureSp('checkoutPlusProducts')}
                loading={isPending}
              >
                Configure
              </Action>
            }
          />
          <SetupStep
            icon={faRocketLaunch}
            learnMoreUrl="https://help.corso.com/checkout-plus/checkout-plus-setup/"
            title="Enable Checkout Plus"
            description="Turn on Checkout Plus to start protecting orders at checkout."
            complete={!!isCheckoutPlusEnabled}
            action={<Badge variant="warning">Pending</Badge>}
          />
        </div>
      </Disclosure>
    </Card>
  );
}

function ProtectionVariants({
  show,
  onClose,

  shipProtectSet,
}: {
  show: boolean;
  onClose: () => void;

  shipProtectSet:
    | CrewMerchantUi.SettingsConfig['shippingProtection']['shipProtectSet']
    | undefined;
}) {
  const isCorsoAdmin = useIsCorsoAdmin();
  const { currencyCode } = useCurrency();
  if (!shipProtectSet) return null;
  const variants = shipProtectSet.units;
  const {
    idFromPlatform,
    platformUrl: productPlatformUrl,
    title: productTitle,
  } = shipProtectSet.product;

  return (
    <Modal
      onClose={onClose}
      show={show}
      title="Checkout Plus Product Variants"
      size="lg"
    >
      {isCorsoAdmin && (
        <>
          <Alert heading="Corso Admins Only">
            The Ship Protect Set details are only visible to Corso admin users.
          </Alert>

          <Card>
            <Card.Heading>Ship Protect Set</Card.Heading>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <Label>Name</Label>
                <p className="text-sm text-neutral-700">
                  {shipProtectSet.name}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <Label>Code</Label>
                <p className="text-sm text-neutral-700">
                  {shipProtectSet.code}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <Label>Shopify Product</Label>
                <div className="flex items-center gap-2">
                  {productPlatformUrl ? (
                    <Action
                      to={productPlatformUrl}
                      target="_blank"
                      variant="link"
                      className="text-xs"
                      icon="shopify"
                      accessibilityLabel="View product on Shopify"
                    >
                      {productTitle}
                    </Action>
                  ) : (
                    <p className="text-sm text-neutral-700">{productTitle}</p>
                  )}
                  <ClipboardButton
                    variant="ghost"
                    onClick={() => idFromPlatform}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Label>Total Units</Label>
                <p className="text-sm text-neutral-700">{variants.length}</p>
              </div>
              <div className="flex flex-col gap-1">
                <Label>Min Protection Value</Label>
                <p className="text-sm text-neutral-700">
                  {formatter.currency(shipProtectSet.minProtectionValue, 'USD')}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <Label>Max Protection Value</Label>
                <p className="text-sm text-neutral-700">
                  {formatter.currency(shipProtectSet.maxProtectionValue, 'USD')}
                </p>
              </div>
            </div>
          </Card>
        </>
      )}
      <div className="flex flex-col gap-4">
        <Card>
          <Card.Heading>Product Variants</Card.Heading>
          {variants.map((v) => (
            <OrderLineItem
              key={v.idFromPlatform}
              title={v.productVariant?.title}
              src={v.productVariant?.imgUrl}
              sku={v.productVariant?.sku}
              price={{
                currencyCode,
                unitPrice: v.productVariant?.price
                  ? parseFloat(v.productVariant.price)
                  : 0,
              }}
            >
              <OrderLineItem.Detail>
                <ClickToCopy value={v.idFromPlatform}>
                  ID: {v.idFromPlatform}
                </ClickToCopy>
                {v.productVariant?.deletedOnPlatform && (
                  <Badge variant="danger">Deleted</Badge>
                )}
              </OrderLineItem.Detail>
              {isCorsoAdmin && (
                <OrderLineItem.Detail>
                  Cart Value Range: {formatter.currency(v.rangeStart, 'USD')} -{' '}
                  {formatter.currency(v.rangeEnd, 'USD')}
                </OrderLineItem.Detail>
              )}
            </OrderLineItem>
          ))}
        </Card>

        <Action onClick={onClose}>Close</Action>
      </div>
    </Modal>
  );
}

export default function CheckoutPlusSettingsPage() {
  const formRef = useRef<HTMLFormElement>(null);

  const isCorsoAdmin = useIsCorsoAdmin();
  const storeId = useStoreId();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showActivateEmbedModal, setShowActivateEmbedModal] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showProtectionVariants, setShowProtectionVariants] = useState(false);

  const [pendingAction, setPendingAction] = useState<
    'checkoutPlusScript' | 'checkoutPlusProducts' | undefined
  >(undefined);

  const { data } = useConfigSettings(
    ({ shippingProtection }) => shippingProtection,
  );
  const { data: checkoutPlusEmbed, isPending: isEmbedPending } =
    useCheckoutPlusEmbed();
  const isCheckoutPlusEmbedScriptConfigured = checkoutPlusEmbed?.isActive;

  const methods = useCheckoutPlusForm();

  const { mutateAsync: saveChanges } = useConfigSettingsUpdate();

  const submitHandler: FormEventHandler = (event) => {
    methods
      .handleSubmit(
        (values) =>
          saveChanges({ shippingProtection: values }).then(() => {
            // reset only after success
            methods.reset();
          }),
        (errors) => {
          console.error(errors);
        },
      )(event)
      .catch(console.error);
  };

  const { mutate: configureSp, isPending } = useConfigureGsp();

  const {
    isCheckoutPlusEnabled,
    isCheckoutPlusScriptConfigured,
    shipProtectSet,
    accessScopes,
    checkoutPlusScriptUrl,
    activateCheckoutPlusEmbedUrl,
    widgetConfig,
  } = data ?? {};

  const hasMetaobjectAccessScope = accessScopes?.includes(
    'unauthenticated_read_metaobjects',
  );

  const hasReadThemes = accessScopes?.includes('read_themes') ?? false;

  const configuredProducts =
    shipProtectSet?.units.filter(
      (u) => u.implementationType === 'Product_Variant',
    ).length ?? 0;

  const checkoutAction = methods.watch(
    'widgetConfig.checkoutPlus.checkoutAction',
  );

  const isEitherScriptConfigured =
    isCheckoutPlusScriptConfigured || isCheckoutPlusEmbedScriptConfigured;

  const areProductsConfigured =
    checkoutAction === 'addVariantToCart' ? configuredProducts > 0 : true;

  const isSetupComplete =
    !!isEitherScriptConfigured &&
    areProductsConfigured &&
    !!isCheckoutPlusEnabled;

  const cartChangeDetectionMethod = methods.watch(
    'widgetConfig.checkoutPlus.cartChangeDetectionMethod',
  );

  const factValue = useFactValues();

  const countryCodeOptions = factValue.data?.definite['order/country'] ?? [];

  const hasCustomScriptTagPlacement =
    !!widgetConfig?.checkoutPlus.scriptTagNote &&
    widgetConfig.checkoutPlus.scriptTagNote !== '';

  const hasRequiredScopes = hasMetaobjectAccessScope && hasReadThemes;

  return (
    <FormProvider {...methods}>
      <Page
        title="Checkout Plus"
        secondaryActions={[
          ...(isCorsoAdmin
            ? [
                {
                  id: 'show-advanced-settings',
                  icon: 'settings' as const,
                  accessibilityLabel: 'Advanced Settings',
                  onAction: () => setShowAdvancedSettings(true),
                },
              ]
            : []),

          ...(isCheckoutPlusEnabled
            ? [
                {
                  id: 'disable-checkout-plus',
                  content: 'Turn Off',
                  onAction: () => {
                    methods.setValue('isCheckoutPlusEnabled', false, {
                      shouldDirty: true,
                    });
                    methods.setValue(
                      'widgetConfig.checkoutPlus.isEnabled',
                      false,
                      {
                        shouldDirty: true,
                      },
                    );
                    formRef.current?.requestSubmit();
                  },
                },
              ]
            : []),
        ]}
        primaryAction={
          isCheckoutPlusEnabled
            ? undefined
            : {
                onAction: () => {
                  methods.setValue('isCheckoutPlusEnabled', true, {
                    shouldDirty: true,
                  });
                  methods.setValue(
                    'widgetConfig.checkoutPlus.isEnabled',
                    true,
                    {
                      shouldDirty: true,
                    },
                  );
                  formRef.current?.requestSubmit();
                },
                content: 'Turn On',
              }
        }
      >
        <form
          ref={formRef}
          className="flex flex-col gap-4"
          id={checkoutPlusSettingsFormId}
          onSubmit={submitHandler}
          onReset={(e) => {
            e.preventDefault();
            methods.reset();
          }}
        >
          {!hasRequiredScopes && (
            <Alert elevated heading="App Upgrade Required" variant="warning">
              Please upgrade the Corso app from within the Shopify App Store, to
              ensure the Checkout Plus functionality works correctly.
            </Alert>
          )}
          <div className="flex flex-col gap-4">
            <FloatingSave
              isDirty={methods.formState.isDirty}
              isSubmitting={methods.formState.isSubmitting}
              form={checkoutPlusSettingsFormId}
            />
          </div>

          {!isEmbedPending && isSetupComplete && (
            <Card>
              {isCheckoutPlusEmbedScriptConfigured && (
                <Card.Line>
                  <Card.Heading>Checkout Plus App Embed</Card.Heading>
                  <Badge variant="success">Configured on Shopify</Badge>
                </Card.Line>
              )}

              {isCheckoutPlusScriptConfigured && (
                <Card.Line>
                  <Card.Heading>Checkout Plus Legacy Script</Card.Heading>
                  <div className="flex items-center gap-2">
                    {checkoutPlusScriptUrl && (
                      <ClipboardButton
                        onClick={() =>
                          `<script async src="${checkoutPlusScriptUrl}"></script>`
                        }
                        variant="ghost"
                      />
                    )}
                    {isPending && pendingAction === 'checkoutPlusScript' ? (
                      <SpinnerIcon />
                    ) : (
                      <Badge variant="success">
                        {hasCustomScriptTagPlacement
                          ? 'Custom Configuration'
                          : 'Configured on Shopify'}
                      </Badge>
                    )}
                  </div>
                </Card.Line>
              )}

              {checkoutAction === 'addVariantToCart' && (
                <div className="flex flex-row items-center justify-between gap-4">
                  <Label>Corso Variants In Inventory</Label>
                  {isPending && pendingAction === 'checkoutPlusProducts' ? (
                    <SpinnerIcon />
                  ) : (
                    <div className="flex items-center gap-2">
                      {configuredProducts > 0 && (
                        <Action
                          icon="eye"
                          variant="ghost"
                          accessibilityLabel="View Product Variant Ids"
                          onClick={() => setShowProtectionVariants(true)}
                        />
                      )}
                      <Badge
                        variant={configuredProducts > 0 ? 'success' : 'warning'}
                      >
                        {configuredProducts}
                      </Badge>
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}
          {!isEmbedPending && !isSetupComplete && (
            <SimpleSetupSteps
              isEmbedConfigured={!!isCheckoutPlusEmbedScriptConfigured}
              onActivateEmbed={() => setShowActivateEmbedModal(true)}
              configuredProducts={configuredProducts}
              isCheckoutPlusEnabled={!!isCheckoutPlusEnabled}
            />
          )}

          {/* general settings */}
          <SettingsAnchorCard id="display-countries">
            <Controller
              control={methods.control}
              name="widgetConfig.checkoutPlus.displayCountryCodes"
              render={({ field: f, fieldState }) => (
                <MultiSelect
                  togglable
                  label="Display Countries"
                  options={countryCodeOptions}
                  details="The countries where the Checkout Plus widget will be displayed."
                  value={
                    f.value
                      ?.map((v) =>
                        countryCodeOptions
                          .filter((o) => 'value' in o)
                          .find((o) => o.value === v),
                      )
                      .filter(isTruthy) ?? []
                  }
                  onChange={(o) => f.onChange(o.map((v) => v.value))}
                  error={fieldState.error?.message}
                />
              )}
            />

            {checkoutAction === 'addVariantToCart' && (
              <>
                <Alert
                  heading="Using Recharge Subscriptions? Please use Corso Recharge integration instead"
                  variant="tip"
                />

                <Controller
                  control={methods.control}
                  name="widgetConfig.checkoutPlus.areSubscriptionsEnabled"
                  render={({ field: { onChange, value }, fieldState }) => (
                    <SwitchInput
                      id="are-subscriptions-enabled"
                      label="Subscriptions"
                      details="When enabled, Checkout Plus will attempt to use a Corso product with a matching selling plan interval."
                      checked={!!value}
                      onChange={onChange}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </>
            )}
          </SettingsAnchorCard>

          <CheckoutWidgetCard returnTo={`/${storeId}/settings/checkout-plus`} />

          {/* existing brand owned checkout button setup */}
          <SettingsAnchorCard id="checkout-button">
            <Card.Heading>Checkout Button</Card.Heading>
            <Controller
              name="widgetConfig.checkoutPlus.checkoutAction"
              control={methods.control}
              render={({ field: { onChange, value }, fieldState }) => (
                <SimpleSelect
                  label="Action"
                  options={checkoutActionOptions}
                  value={value} // be warned, react-hook-form is lying about the value type, as there's no default value
                  onChange={onChange}
                  error={fieldState.error?.message}
                  required
                />
              )}
            />

            <TextAreaInput
              id="checkout-button-text"
              label="Content"
              details={`The text or HTML to display within the checkout button. Supports the following template variables: ${formatter.list.and(
                Object.values(CheckoutPlusTemplateVariable).map(
                  (v) => `{{${v}}}`,
                ),
              )}.`}
              {...methods.register(
                'widgetConfig.checkoutPlus.checkoutButtonText',
              )}
              error={
                methods.formState.errors.widgetConfig?.checkoutPlus
                  ?.checkoutButtonText?.message
              }
            />

            <Controller
              control={methods.control}
              name="widgetConfig.checkoutPlus.checkoutSelectors"
              render={({ field: f, fieldState }) => (
                <MultiSelect
                  creatable
                  label="Selectors"
                  options={[]}
                  details="CSS selectors for the checkout elements on your store. The elements selected must be clickable. If no selectors are provided, default selectors will be used."
                  placeholder="Add Selector"
                  value={
                    f.value?.map((v) => ({
                      value: v.toString(),
                      label: v,
                    })) ?? []
                  }
                  onChange={(o) => f.onChange(o.map((v) => v.value))}
                  error={fieldState.error?.message}
                />
              )}
            />

            <Card.Heading>Cart</Card.Heading>

            <Controller
              name="widgetConfig.checkoutPlus.cartChangeDetectionMethod"
              control={methods.control}
              render={({ field: { onChange, value }, fieldState }) => (
                <SimpleSelect
                  label="Cart Change Detection Method"
                  options={cartChangeDetectionMethodOptions}
                  value={value} // be warned, react-hook-form is lying about the value type, as there's no default value
                  onChange={onChange}
                  error={fieldState.error?.message}
                  required
                />
              )}
            />

            {cartChangeDetectionMethod === 'selectorObserver' && (
              <Controller
                control={methods.control}
                name="widgetConfig.checkoutPlus.cartSelectors"
                render={({ field: f, fieldState }) => (
                  <MultiSelect
                    creatable
                    label="Selectors"
                    options={[]}
                    details="The CSS selectors for the cart on your store. If no selectors are provided, default selectors will be used."
                    placeholder="Add Selector"
                    value={
                      f.value?.map((v) => ({
                        value: v.toString(),
                        label: v,
                      })) ?? []
                    }
                    onChange={(o) => f.onChange(o.map((v) => v.value))}
                    error={fieldState.error?.message}
                  />
                )}
              />
            )}
          </SettingsAnchorCard>

          <SettingsAnchorCard id="custom-css">
            <Card.Heading>Custom CSS</Card.Heading>
            <TextAreaInput
              id="checkout-plus-custom-css"
              label="Custom CSS"
              details="The styles to apply as overrides to the default Checkout Plus styles."
              {...methods.register('widgetConfig.checkoutPlus.customCss')}
              error={
                methods.formState.errors.widgetConfig?.checkoutPlus?.customCss
                  ?.message
              }
            />
            <Action
              variant="default"
              onClick={() =>
                methods.setValue(
                  `widgetConfig.checkoutPlus.customCss`,
                  formatCss(
                    methods.getValues(`widgetConfig.checkoutPlus.customCss`) ??
                      '',
                  ),
                  {
                    shouldDirty: true,
                  },
                )
              }
            >
              Format CSS
            </Action>
          </SettingsAnchorCard>

          {/* checkout plus elements */}
          <CheckoutPlusElements />

          <ConfirmModal
            prompt={
              isCheckoutPlusScriptConfigured
                ? 'Activating the Checkout Plus app embed on your theme will also remove the legacy checkout script.'
                : 'This will activate the Checkout Plus app embed on your theme.'
            }
            title="Activate Checkout Plus Embed"
            show={showActivateEmbedModal}
            loading={isPending && pendingAction === 'checkoutPlusScript'}
            confirmText="Activate"
            variant="caution"
            onConfirm={() => {
              if (isCheckoutPlusScriptConfigured) {
                setPendingAction('checkoutPlusScript');
                configureSp('deleteCheckoutPlusScript');
              }
              window.open(String(activateCheckoutPlusEmbedUrl), '_blank');
              setShowActivateEmbedModal(false);
            }}
            onCancel={() => setShowActivateEmbedModal(false)}
          />

          <ConfirmModal
            prompt="This will configure the products for use with Checkout Plus, if the products already exist they will be recreated."
            title="Configure Products"
            show={showConfirmationModal}
            loading={isPending && pendingAction === 'checkoutPlusProducts'}
            confirmText="Confirm"
            variant="caution"
            onConfirm={() => {
              setPendingAction('checkoutPlusProducts');
              configureSp('checkoutPlusProducts');
              setShowConfirmationModal(false);
            }}
            onCancel={() => setShowConfirmationModal(false)}
          />
        </form>

        <ProtectionVariants
          show={showProtectionVariants}
          onClose={() => setShowProtectionVariants(false)}
          shipProtectSet={shipProtectSet ?? undefined}
        />
      </Page>
      <AdvancedSettings
        show={showAdvancedSettings}
        onClose={() => setShowAdvancedSettings(false)}
        isCheckoutPlusScriptConfigured={!!isCheckoutPlusScriptConfigured}
        checkoutPlusScriptUrl={checkoutPlusScriptUrl}
      />
    </FormProvider>
  );
}
